const NIELSEN_MEDIAMETRIE_NAME = 'nielsenMediametrie';

const EngineStatus = {
  DEFAULT: 'default',
  PLAYING: 'playing',
  PAUSED: 'paused',
  SEEKING: 'seeking',
};

const NielsenEvent = {
  SET_PLAYHEAD_POSITION: 'setPlayheadPosition',
  STOP: 'stop',
  END: 'end',
  LOAD_METADATA: 'loadMetadata',
};

export const DEFAULT_ATTRIBUTES = ['currentTime', 'contentType'];

const IS_LIVE_KEY = 'nol_c18';

class NielsenMediametrie {
  constructor() {
    this.name = NIELSEN_MEDIAMETRIE_NAME;
    this.instance = undefined;
    this.status = EngineStatus.DEFAULT;
    this.metadata = {};
    this.lastSentEvent = undefined;
    this.isActive = () => !!this.instance;
    this.time = undefined;
    this.isFirstPlayheadSent = false;
    this.isLive = false;
    this.isAudio = false;
    this.shouldLoadMetadataAfterInterruption = false;
    this.sdkDebugEnabled = false;
  }

  init(config) {
    const {
      appId,
      instanceName,
      metadata,
      shouldLoadMetadataAfterInterruption = false,
      sdkDebugEnabled = false,
      shouldTrackAudio = false,
    } = config;

    this._attributesKeys = DEFAULT_ATTRIBUTES;

    const additionalParams = {
      ...(sdkDebugEnabled && { nol_sdkDebug: 'debug' }),
    };

    this.instance = window.NOLBUNDLE.nlsQ(appId, instanceName, additionalParams);
    this.metadata = metadata;
    this.status = EngineStatus.DEFAULT;
    this.lastSentEvent = undefined;
    this.time = undefined;
    this.isFirstPlayheadSent = false;
    this.shouldLoadMetadataAfterInterruption = shouldLoadMetadataAfterInterruption;
    this.shouldTrackAudio = shouldTrackAudio;
  }

  checkIsLive() {
    if (!this.metadata) return;
    this.isLive = /,Y$/.test(this.metadata[IS_LIVE_KEY]);
  }

  set currentTime(value) {
    if (!Number.isFinite(value) || (this.isAudio && !this.shouldTrackAudio)) {
      return;
    }

    const latestTime = Math.max(0, Math.floor(this.isLive ? Date.now() / 1000 : value));

    if (this.time !== latestTime) {
      this.sendPlayheadEvent(latestTime);
    }
  }

  set contentType(value) {
    this.isAudio = ['radio', 'audio'].includes(value);
  }

  setCustomAttributes(attributes) {
    Object.keys(attributes)
      .filter(key => this._attributesKeys.includes(key))
      .forEach(key => {
        this[key] = attributes[key];
      });
  }

  sendMetadata() {
    this.instance.ggPM(NielsenEvent.LOAD_METADATA, this.metadata);
    this.lastSentEvent = NielsenEvent.LOAD_METADATA;
  }

  onLoadedData(metadata) {
    this.metadata = metadata;
    this.checkIsLive();
    // this.sendMetadata();
  }

  onPlaying() {
    if (this.shouldLoadMetadataAfterInterruption && this.lastSentEvent !== NielsenEvent.LOAD_METADATA) {
      this.sendMetadata();
    }
    this.status = EngineStatus.PLAYING;
  }

  onPause() {
    this.sendStopEvent();
    this.status = EngineStatus.PAUSED;
  }

  onBuffering() {
    this.sendStopEvent();
    this.status = EngineStatus.PAUSED;
  }

  onEnded() {
    this.sendEndEvent();
    this.status = EngineStatus.DEFAULT;
  }

  onStop() {
    this.sendEndEvent();
    this.status = EngineStatus.DEFAULT;
  }

  onSeeking() {
    this.status = EngineStatus.SEEKING;
  }

  // Only used for TV player as they cannot dispatch a Seeking event on setSeekTime
  onSeekTimeUpdate() {
    this.status = EngineStatus.SEEKING;
  }

  onSeeked(params) {
    this.sendStopEvent(params.currentTime);
    this.status = EngineStatus.PLAYING;
  }

  onUpdatePlayerConfig(params) {
    const { currentTime, analytics } = params;
    this.sendEndEvent(currentTime);
    // @ts-expect-error waiting for BE to update the analytics type
    this.metadata = analytics[this.name].metadata;
    this.instance.ggPM(NielsenEvent.LOAD_METADATA, this.metadata);
    this.lastSentEvent = NielsenEvent.LOAD_METADATA;
  }

  // eslint-disable-next-line no-unused-vars
  sendEvent(params, action, eventFields, attributesByThirdParties) {
    if (this.isAudio && !this.shouldTrackAudio) return;

    switch (params.key) {
      case 'playbackStarted':
        if (params.metadata) {
          this.onLoadedData(params.metadata);
        }
        break;
      case 'playVideo':
        this.onPlaying();
        break;
      case 'pauseVideo':
        this.onPause();
        break;
      case 'playbackStopped':
        this.onEnded();
        break;
      case 'buffering':
        this.onBuffering();
        break;
      case 'bufferingEnded':
        this.onPlaying();
        break;
      case 'complete':
        this.onEnded();
        break;
      case 'replayNextVideo':
        this.sendMetadata();
        break;
      case 'midrollBegins':
        // send stop event before midroll begins
        // since there is no other event between playVideo and midrollBegins
        this.onPause();
        break;
      default:
        break;
    }
  }

  sendPlayheadEvent(time) {
    if (this.status !== EngineStatus.PLAYING) {
      return;
    }
    this.time = time;

    this.instance.ggPM(NielsenEvent.SET_PLAYHEAD_POSITION, this.time);
    this.lastSentEvent = NielsenEvent.SET_PLAYHEAD_POSITION;
    if (!this.isFirstPlayheadSent) {
      this.isFirstPlayheadSent = true;
    }
  }

  sendStopEvent() {
    if (this.lastSentEvent === NielsenEvent.STOP || !this.isFirstPlayheadSent) return;
    this.instance.ggPM(NielsenEvent.STOP, this.time);
    this.lastSentEvent = NielsenEvent.STOP;
  }

  sendEndEvent() {
    if (this.lastSentEvent === NielsenEvent.END) return;
    this.instance.ggPM(NielsenEvent.END, this.time);
    this.lastSentEvent = NielsenEvent.END;
    this.isFirstPlayheadSent = false;
  }

  release() {
    this.instance = undefined;
    this.status = EngineStatus.DEFAULT;
    this.metadata = {};
    this.isAudio = false;
    this.time = undefined;
  }
}
export default new NielsenMediametrie();
